import {
  Autocomplete,
  Box,
  BoxStatus,
  Button,
  ComposedChart,
  DateRangeInput,
  Flexbox,
  GraphicFilters,
  Label,
  Select,
  shootToast,
  Stack,
  StackRow,
} from "codekit";
import {
  useGenerateExcelTeincomeMutation,
  useGetDataGraphicTeincomeMutation,
  useGetDataGroupsTeincomeMutation,
} from "../../../../libs/redux/slices/Graficsx/Teincome/teincomeApiSlice";
import { useCallback, useEffect, useState } from "react";
import { useGetClientGraphicByIdMutation } from "../../../../libs/redux/slices/Reportxx/Repfinac/repfinacApiSlice";
import { Controller, useForm, useWatch } from "react-hook-form";
import { getActualDate } from "../../../../utils/globalFunctions";
import {
  formatearFecha,
  getRangePersonalized,
  obtenerAñoActual,
  obtenerAñoAnterior,
  obtenerMesActual,
  obtenerMesAnterior,
  obtenerTrimestreActual,
  obtenerTrimestreAnterior,
} from "./utils/TeincomeUtil";

/**
 * Componente para el pintado de la grafica de tendencia de ingresos por gerencia
 */
const Teincome = ({ graphName }) => {
  const [getClientById] = useGetClientGraphicByIdMutation();
  const [resultsClient, setResultsClient] = useState([]);
  const [getDataGraphic] = useGetDataGraphicTeincomeMutation();
  const [getDataGroupsTeincome] = useGetDataGroupsTeincomeMutation();
  const [generateExcelTeincome] = useGenerateExcelTeincomeMutation();
  const [maxValue, setMaxValue] = useState(0);
  const [tickts, setTickts] = useState([]);
  const [dataGraphic, setDataGraphic] = useState([]);
  const actualDay = getActualDate();
  const lastDayPrevMonth = new Date(actualDay);
  lastDayPrevMonth.setMonth(lastDayPrevMonth.getMonth() - 1);
  const [firstRange, setFirstRange] = useState(null);
  const [secondRange, setSecondRange] = useState(null);
  const [valueStartDate, setValueStartDate] = useState([]);
  const [valueEndDate, setValueEndDate] = useState([]);

  const { control, reset, setValue } = useForm({
    mode: "onBlur",
    defaultValues: {
      FECHAXXX: actualDay,
      TIPOFILT: "FACTURACION",
      CCOIDXXX: "",
      COMPARAR: "MES",
      TIPOCLIX: "OPERACION",
    },
  });
  const values = useWatch({ control });
  const [dataGraphicGroups, setDataGraphicGroups] = useState([]);
  const [isGraphicGroups, setGraphicGroups] = useState(false);
  const [comparative, setComparative] = useState("MES");

  const searchClient = async (CLIIDXXX, cb) => {
    return await getClientById({
      filters: { CLIIDXXX },
    })
      .unwrap()
      .then((response) => {
        setResultsClient(response.data);
      })
      .catch((err) => {
        console.log("entro en el catch", err);
      })
      .finally(() => {
        cb();
      });
  };

  //Funcion para generar las divisiones de acuerdo al valor maximo
  function generateTickts(maxValue, partes = 10) {
    const step = maxValue / partes;
    const result = [];
    for (let i = 0; i <= partes; i++) {
      result.push(Math.round(i * step));
    }
    return result;
  }

  //Funcion para agregar filtro de fecha inicial segun el tipo de comparacion escojido
  const setInitialDateFilter = useCallback((comparative, range) => {
    let infoDate = null;
    switch (comparative) {
      case "AÑO":
        infoDate = obtenerAñoAnterior();
        break;
      case "TRIMESTRE":
        infoDate = obtenerTrimestreAnterior();
        break;
      case "PERSONALIZADO":
        infoDate = getRangePersonalized(range);
        break;
      default:
        infoDate = obtenerMesAnterior();
        break;
    }
    return infoDate;
  }, []);

  //Funcion para agregar filtro de fecha final segun el tipo de comparacion escojido
  const setFinalDateFilter = useCallback((comparative, range) => {
    let infoDate = null;
    switch (comparative) {
      case "AÑO":
        infoDate = obtenerAñoActual();
        break;
      case "TRIMESTRE":
        infoDate = obtenerTrimestreActual();
        break;
      case "PERSONALIZADO":
        infoDate = getRangePersonalized(range);
        break;
      default:
        infoDate = obtenerMesActual();
        break;
    }
    return infoDate;
  }, []);

  //Funcion que retorna los filtros que se aplican siempre
  const getFilters = useCallback(
    (values, comparative, ranges) => {
      let filters = {};
      const initialDates = setInitialDateFilter(comparative, ranges[0]);
      const finalDates = setFinalDateFilter(comparative, ranges[1]);
      filters = {
        ...values,
        CLIIDXXX: values.CLIIDXXX ? values.CLIIDXXX.split(" - ")[0] : "",
        FECHINIT: [initialDates.initialDate, initialDates.finalDate],
        FECHFINT: [finalDates.initialDate, finalDates.finalDate],
      };
      return { filters, initialDates, finalDates };
    },
    [setFinalDateFilter, setInitialDateFilter]
  );

  //Funcion para convertir data a informacion que acepte la grafica
  const mapDataGrphic = (data, initialDates, finalDates) => {
    return data[1].map((item, index) => {
      let numPrevious = 0;
      if (data[0][index]) {
        numPrevious = data[0][index].MDTCVLRX;
      }
      return {
        name: item.CCODESXX,
        previousDate: initialDates.range,
        currentDate: finalDates.range,
        numCurrenDate: `${item.MDTCVLRX}`,
        numPreviousDate: `${numPrevious}`,
        CCOIDXXX: item.CCOIDXXX,
      };
    });
  };

  //Funcion para la consulta de informacion de la Grafica
  const handleGetData = useCallback(
    async (values, comparative, ranges) => {
      const { filters, initialDates, finalDates } = getFilters(values, comparative, ranges);
      await getDataGraphic({ filters })
        .unwrap()
        .then((response) => {
          const { data } = response;
          let valueMax = 0;
          data.forEach((items) => {
            items.forEach((element) => {
              valueMax = valueMax < element.MDTCVLRX ? element.MDTCVLRX : valueMax;
            });
          });
          const dataMap = mapDataGrphic(data, initialDates, finalDates);
          setDataGraphic(dataMap);
          setMaxValue(valueMax);
          setTickts(generateTickts(valueMax));
        })
        .catch();
    },
    [getDataGraphic, getFilters]
  );

  //Funcion para la generacion del excel
  const handleGenerateExcel = async (context) => {
    let ranges = [];
    if (comparative !== "PERSONALIZADO" && firstRange && secondRange) {
      ranges = [firstRange, secondRange];
    }
    let { filters } = getFilters(values, comparative, ranges);
    if (context) {
      const {
        data: { CCOIDXXX, GRUPIDXX },
      } = context;
      filters = { ...filters, CCOIDXXX, GRUPIDXX };
    }
    await generateExcelTeincome({ filters })
      .unwrap()
      .then(() => {
        shootToast({
          variant: "success",
          text: "Tu reporte se esta generando! \n En Pocos minutos recibiras las confirmación del mismo por correo electronico",
          duration: 5000,
        });
      })
      .catch();
  };

  /**
   * Funcion para consultar los ingresos por grupos de una gerencia
   */
  const handleViewGroups = useCallback(
    async (values, comparative, ranges) => {
      const { filters, initialDates, finalDates } = getFilters(values, comparative, ranges);
      await getDataGroupsTeincome({ filters })
        .unwrap()
        .then((response) => {
          const { data } = response;
          let valueMax = 0;
          data.forEach((items) => {
            items.forEach((element) => {
              valueMax = valueMax < element.MDTCVLRX ? element.MDTCVLRX : valueMax;
            });
          });
          const dataMap = mapDataGrphic(data, initialDates, finalDates);
          setDataGraphicGroups(dataMap);
          setMaxValue(valueMax);
          setTickts(generateTickts(valueMax));
        })
        .catch();
    },
    [getDataGroupsTeincome, getFilters]
  );

  //Hook para volver a realizar consulta cuando cambia algun filtro
  useEffect(() => {
    const filters = { ...values };
    if (comparative !== "PERSONALIZADO") {
      if (!isGraphicGroups) {
        handleGetData(filters, comparative, []);
      } else {
        handleViewGroups(filters, comparative, []);
      }
    } else {
      if (firstRange && secondRange) {
        if (!isGraphicGroups) {
          handleGetData(filters, comparative, [firstRange, secondRange]);
        } else {
          handleViewGroups(filters, comparative, [firstRange, secondRange]);
        }
      }
    }
  }, [handleGetData, values, isGraphicGroups, handleViewGroups, comparative, firstRange, secondRange]);

  //Funcion para formatear el primer rango a un fecha valida
  const changeStartRange = (context, type = "start") => {
    const { option } = context;
    const startDate = formatearFecha(option.startDate);
    const endDate = formatearFecha(option.endDate);
    if (type === "start") {
      setValueStartDate([startDate.replaceAll("-", "/"), endDate.replaceAll("-", "/")]);
      setFirstRange(option);
    } else {
      setValueEndDate([startDate.replaceAll("-", "/"), endDate.replaceAll("-", "/")]);
      setSecondRange(option);
    }
  };

  return (
    <>
      <Box spacing={4}>
        <GraphicFilters
          containerName={graphName}
          graphicKeyName="ingeprop"
          clearFilters={() => {
            reset();
            setGraphicGroups(false);
          }}
          onClickVerMas={() => {
            handleGenerateExcel();
          }}
        >
          <Box>
            <Box mb={8} borderBottom={1} spacing={2}>
              <Stack gap={3} columnsVariant="form-3">
                <StackRow>
                  <Label>Cliente</Label>
                  <Controller
                    control={control}
                    name={`CLIIDXXX`}
                    rules={{ required: true }}
                    render={({ field: { onChange, value } }) => (
                      <Autocomplete
                        placeholder="Buscar..."
                        textLoading="Buscando..."
                        textNoResults="No se encontraron registros"
                        minLengthToSearch={2}
                        selected={value}
                        searchResults={resultsClient}
                        onChangeSearch={(value, cb) => searchClient(value, cb)}
                        onItemClick={(context) => {
                          onChange(`${context.item.label}`);
                        }}
                        onResetSelected={() => {
                          onChange("");
                        }}
                      />
                    )}
                  />
                </StackRow>
                <StackRow>
                  <Label>Tipo de Cliente</Label>
                  <Controller
                    control={control}
                    name={`TIPOCLIX`}
                    rules={{ required: true }}
                    render={({ field: { onChange, value } }) => (
                      <Select
                        value={value}
                        options={[
                          { text: "SELECCIONE", value: "" },
                          { text: "CLIENTE DE LA OPERACION", value: "OPERACION" },
                          { text: "A QUIEN VA DIRIGIDA LA FACTURA", value: "FACTURA" },
                        ]}
                        onChangeValue={(context) => {
                          onChange(context.option.value);
                        }}
                      />
                    )}
                  />
                </StackRow>
                <StackRow>
                  <Label>Tipo de Operación</Label>
                  <Controller
                    control={control}
                    name={`DOCTIPXX`}
                    rules={{ required: true }}
                    render={({ field: { onChange, value } }) => (
                      <Select
                        value={value}
                        options={[
                          { text: "SELECCIONE", value: "" },
                          { text: "IMPORTACION", value: "IMPORTACION" },
                          { text: "EXPORTACION", value: "EXPORTACION" },
                          { text: "REGISTRO", value: "REGISTRO" },
                          { text: "TRANSITO", value: "TRANSITO" },
                          { text: "OTROS", value: "OTROS" },
                        ]}
                        onChangeValue={(context) => {
                          onChange(context.option.value);
                        }}
                      />
                    )}
                  />
                </StackRow>
              </Stack>
              <Box mt={8}>
                <Flexbox gap={7} direction="row" wrap>
                  <Button
                    variant={comparative === "MES" ? "secondary-bold" : "secondary-light"}
                    onClick={() => setComparative("MES")}
                  >
                    vs Mes Anterior
                  </Button>
                  <Button
                    variant={comparative === "TRIMESTRE" ? "secondary-bold" : "secondary-light"}
                    onClick={() => setComparative("TRIMESTRE")}
                  >
                    vs Trimestre Anterior
                  </Button>
                  <Button
                    variant={comparative === "AÑO" ? "secondary-bold" : "secondary-light"}
                    onClick={() => setComparative("AÑO")}
                  >
                    vs Año Anterior
                  </Button>
                  <Button
                    variant={comparative === "PERSONALIZADO" ? "secondary-bold" : "secondary-light"}
                    onClick={() => setComparative("PERSONALIZADO")}
                  >
                    Personalizado
                  </Button>
                </Flexbox>
              </Box>
              {comparative === "PERSONALIZADO" && (
                <Box mt={8}>
                  <Stack columnsVariant="form-4">
                    <StackRow>
                      <Label>Fecha Inicial</Label>
                      <DateRangeInput
                        valueFilterDate={valueStartDate}
                        onChangeValueFilterDate={(context) => changeStartRange(context)}
                      />
                    </StackRow>
                    <StackRow>
                      <Label>Fecha Final</Label>
                      <DateRangeInput
                        valueFilterDate={valueEndDate}
                        onChangeValueFilterDate={(context) => changeStartRange(context, "end")}
                      />
                    </StackRow>
                  </Stack>
                </Box>
              )}
              <Box mt={8}></Box>
            </Box>
            <Box>
              {!isGraphicGroups ? (
                <>
                  {dataGraphic.length > 0 ? (
                    <Stack gap={8}>
                      <StackRow>
                        <ComposedChart
                          data={dataGraphic}
                          chartColors={[{ colorBar: "secondary-600", colorLine: "success-600" }]}
                          domainYAxis={[0, maxValue]}
                          ticksYAxis={tickts}
                          isCurrency={true}
                          onActivClick={(context) => {
                            setGraphicGroups(true);
                            setValue("CCOIDXXX", context?.data?.CCOIDXXX);
                          }}
                        />
                      </StackRow>
                    </Stack>
                  ) : (
                    <BoxStatus variant="empty" text="Esta sección está vacía" />
                  )}
                </>
              ) : (
                <>
                  {dataGraphicGroups.length > 0 ? (
                    <Stack gap={8}>
                      <StackRow>
                        <ComposedChart
                          data={dataGraphicGroups}
                          chartColors={[{ colorBar: "secondary-600", colorLine: "success-600" }]}
                          domainYAxis={[0, maxValue]}
                          ticksYAxis={tickts}
                          isCurrency={true}
                        />
                      </StackRow>
                    </Stack>
                  ) : (
                    <BoxStatus variant="empty" text="Esta sección está vacía" />
                  )}
                </>
              )}
            </Box>
          </Box>
        </GraphicFilters>
      </Box>
    </>
  );
};

export default Teincome;
